import { useState, useEffect, useCallback } from "react";
import { ClipboardList, CheckCircle2, Calendar as CalendarIcon } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { toast } from "sonner";
import apiClient from "../services/api";

/**
 * "My Tasks" popup for the foreman Daily Entry screen.
 * Shows all open to-do items assigned to the foreman until they mark them complete.
 */
const ForemanTasks = ({ onDark = true }) => {
  const [tasks, setTasks] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [completingId, setCompletingId] = useState(null);

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await apiClient.get(`/tasks/mine`);
      setTasks(data.tasks || []);
    } catch (e) {
      // best-effort
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
    const interval = setInterval(fetchTasks, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchTasks]);

  useEffect(() => {
    if (open) fetchTasks();
  }, [open, fetchTasks]);

  const completeTask = async (task) => {
    setCompletingId(task.note_id);
    try {
      await apiClient.put(`/jobs/${task.job_id}/notes/${task.note_id}`, { is_complete: true });
      setTasks((prev) => prev.filter((t) => t.note_id !== task.note_id));
      toast.success("Task marked complete");
    } catch (e) {
      toast.error("Could not complete task");
    } finally {
      setCompletingId(null);
    }
  };

  const count = tasks.length;

  return (
    <>
      <Button
        variant={onDark ? "ghost" : "outline"}
        size="icon"
        className={onDark ? "relative text-primary-foreground hover:bg-primary/80" : "relative text-foreground bg-background"}
        title="Tasks"
        onClick={() => setOpen(true)}
        data-testid="foreman-tasks-btn"
      >
        <ClipboardList className="w-5 h-5" />
        {count > 0 && (
          <Badge
            variant="destructive"
            className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1.5 py-0 text-xs flex items-center justify-center"
            data-testid="foreman-tasks-count"
          >
            {count > 99 ? "99+" : count}
          </Badge>
        )}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="foreman-tasks-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5" /> My Tasks
              {count > 0 && (
                <span className="ml-1 rounded-full bg-primary px-2 py-0.5 text-xs font-medium text-primary-foreground">
                  {count}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          {loading && tasks.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Loading...</p>
          ) : tasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
              <CheckCircle2 className="mb-2 h-8 w-8 opacity-50" />
              <p className="text-sm">No open tasks — you're all caught up!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {tasks.map((task) => (
                <div
                  key={task.note_id}
                  className="rounded-lg border p-3"
                  data-testid={`foreman-task-${task.note_id}`}
                >
                  <p className="text-sm font-medium text-foreground">{task.content}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span className="rounded bg-muted px-1.5 py-0.5">{task.job_name}</span>
                    {task.due_date && (
                      <span className="flex items-center gap-1 text-amber-700">
                        <CalendarIcon className="h-3 w-3" /> Due {task.due_date}
                      </span>
                    )}
                    {task.created_by_name && <span>from {task.created_by_name}</span>}
                  </div>
                  <div className="mt-2">
                    <Button
                      size="sm"
                      onClick={() => completeTask(task)}
                      disabled={completingId === task.note_id}
                      data-testid={`foreman-task-complete-${task.note_id}`}
                    >
                      <CheckCircle2 className="mr-1.5 h-4 w-4" />
                      {completingId === task.note_id ? "Completing..." : "Mark complete"}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ForemanTasks;
